import React, { useState, useEffect } from "react";
import ContactStack from '../ContactPage/ContactPage';
import { Container, Row, Col } from 'react-bootstrap';
import { AnimatePresence } from 'framer-motion';
import Navbar from "../Navigation/Navigation";

import { Mail, Phone, MapPin } from 'lucide-react';

const FullContact = () => {
  const [showContact, setShowContact] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Addresses for the Maps
  const indiaAddress = "Saravanan Towers, Millennium Town Phase 2, Vanagaram, Chennai, Tamil Nadu 600095";
  const malaysiaAddress = "Selangor, Malaysia";
  
  const encodedIndia = encodeURIComponent(indiaAddress);
  const encodedMalaysia = encodeURIComponent(malaysiaAddress);
  
  const indiaMapSrc = `https://maps.google.com/maps?q=${encodedIndia}&t=&z=15&ie=UTF8&iwloc=B&output=embed`;
  const malaysiaMapSrc = `https://maps.google.com/maps?q=${encodedMalaysia}&t=&z=12&ie=UTF8&iwloc=B&output=embed`;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="contact-page-container" style={{ position: 'relative', overflow: 'hidden', background: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .contact-info-link {
          color: #0D1F46;
          text-decoration: none;
          transition: all 0.25s ease-in-out;
          display: inline-block;
        }
        .contact-info-link:hover {
          color: #009BD8 !important;
          transform: translateX(3px);
        }
        .contact-info-card {
          transition: transform 0.3s ease;
        }
        .contact-info-card:hover {
          transform: translateY(-2px);
        }
      `}</style>
      <Navbar />

      <section className="flex-grow-1 d-flex align-items-center pt-5 mt-5">
        <Container fluid className="px-lg-5">
          {/* Main Content Row */}
          <Row className="align-items-center mb-5" >
            {/* Left Column: Heading & Description */}
            <Col lg={5} md={12} className="text-left">
              <h1 className="text-5xl md:text-7xl font-bold mb-3 text-[#001233] leading-[0.85]">
                Let’s Ascent <br /> Together
              </h1>

              <p style={{
                color: '#0D1F46',
                fontSize: isMobile ? '1.1rem' : '1.2rem',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                marginBottom: '2.5rem'
              }}>
                From strategy to execution, we help brands grow with clarity.
              </p>

              <button
                onClick={() => setShowContact(true)}
                style={{
                  backgroundColor: '#0D1F46',
                  color: '#fff',
                  padding: '14px 45px',
                  borderRadius: '50px',
                  border: 'none',
                  fontWeight: '600'
                }}
              >
                Contact Us
              </button>
            </Col>

            {/* Right Column: Maps with Pins and Address Info */}
            <Col lg={7} md={12} className="mt-5 mt-lg-0">
              <Row>
                <Col md={6} className="mb-4 mb-md-0">
                  <h5 className="mb-3" style={{ color: '#0D1F46', fontWeight: '600' }}>India Office</h5>
                  <div className="shadow-sm rounded-4 overflow-hidden" style={{ border: '1px solid #eee' }}>
                    <iframe
                      title="India Office Location"
                      src={indiaMapSrc}
                      width="100%"
                      height={isMobile ? "250" : "350"}
                      style={{ border: 0, display: 'block' }}
                      allowFullScreen=""
                      loading="lazy"
                    ></iframe>
                  </div>
                </Col>
                <Col md={6}>
                  <h5 className="mb-3" style={{ color: '#0D1F46', fontWeight: '600' }}>Malaysia Office</h5>
                  <div className="shadow-sm rounded-4 overflow-hidden" style={{ border: '1px solid #eee' }}>
                    <iframe
                      title="Malaysia Office Location"
                      src={malaysiaMapSrc}
                      width="100%"
                      height={isMobile ? "250" : "350"}
                      style={{ border: 0, display: 'block' }}
                      allowFullScreen=""
                      loading="lazy"
                    ></iframe>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Bottom Info Row */}
          <Row className="border-top pt-4 mt-auto align-items-stretch" style={{ borderTop: '1px solid #eee' }}>
            {/* India Office Column */}
            <Col md={6} xs={12} className="mb-4 mb-md-0 px-md-4" style={{ borderRight: window.innerWidth > 768 ? '1px solid #eee' : 'none' }}>
              <div className="office-block">
                <h5 style={{ color: '#0D1F46', fontWeight: '700', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.95rem' }}>
                  India Office
                </h5>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <MapPin size={18} color="#0D1F46" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <a 
                    href="https://maps.google.com/?q=Saravanan+Towers,+Millennium+Town+Phase+2,+Vanagaram,+Chennai,+Tamil+Nadu+600095"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.9rem', color: '#555', fontWeight: '500', lineHeight: '1.4', textDecoration: 'none' }}
                  >
                    Plot No:2, Saravanan Towers, 2nd Floor, Millennium Town <br/>Phase 2, Vanagaram, Chennai 600095, Tamilnadu, India.
                  </a>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                  <Phone size={18} color="#0D1F46" style={{ flexShrink: 0 }} />
                  <a href="tel:+917540001028" style={{ fontSize: '0.9rem', color: '#555', fontWeight: '500', textDecoration: 'none' }}>
                    75400 01028
                  </a>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <Mail size={18} color="#0D1F46" style={{ flexShrink: 0 }} />
                  <a href="mailto:admin@ascentbw.com" style={{ fontSize: '0.9rem', color: '#555', fontWeight: '500', textDecoration: 'none' }}>
                    admin@ascentbw.com
                  </a>
                </div>
              </div>
            </Col>

            {/* Malaysia Office Column */}
            <Col md={6} xs={12} className="px-md-4">
              <div className="office-block">
                <h5 style={{ color: '#0D1F46', fontWeight: '700', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.95rem' }}>
                  Malaysia Office
                </h5>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <MapPin size={18} color="#0D1F46" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <a 
                    href="https://maps.google.com/?q=Selangor,+Malaysia"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.9rem', color: '#555', fontWeight: '500', lineHeight: '1.4', textDecoration: 'none' }}
                  >
                    Selangor, Malaysia
                  </a>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                  <Phone size={18} color="#0D1F46" style={{ flexShrink: 0 }} />
                  <a href="tel:+601137919920" style={{ fontSize: '0.9rem', color: '#555', fontWeight: '500', textDecoration: 'none' }}>
                    +60 11-3791 9920
                  </a>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <Mail size={18} color="#0D1F46" style={{ flexShrink: 0 }} />
                  <a href="mailto:admin@ascentbw.com" style={{ fontSize: '0.9rem', color: '#555', fontWeight: '500', textDecoration: 'none' }}>
                    admin@ascentbw.com
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <AnimatePresence>
        {showContact && (
          <ContactStack onClose={() => setShowContact(false)} />
        )}
      </AnimatePresence>

    </div>
  );
};

export default FullContact;